import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdBanner from '../components/AdBanner';
import { getBlogPostBySlug } from '../services/api';
import { STATIC_POSTS_DETAIL } from '../data/staticBlogData';
import './BlogPostPage.css';

function renderInline(text) {
  // Bold text
  const parts = [];
  const boldRegex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

function groupTableRows(content) {
  const lines = content.split('\n');
  const result = [];
  let tableRows = [];
  let inTable = false;

  lines.forEach((line, i) => {
    const isTableLine = line.startsWith('| ') && line.includes('|');
    const isSeparator = isTableLine && line.trim().match(/^\|[-| :]+\|$/);

    if (isTableLine) {
      if (!inTable) inTable = true;
      if (!isSeparator) {
        const cells = line.split('|').filter(c => c.trim() !== '');
        tableRows.push(cells.map(c => c.trim()));
      }
    } else {
      if (inTable && tableRows.length > 0) {
        result.push({ type: 'table', rows: tableRows });
        tableRows = [];
        inTable = false;
      }
      result.push({ type: 'line', text: line });
    }
  });

  if (tableRows.length > 0) {
    result.push({ type: 'table', rows: tableRows });
  }

  return result;
}

function renderElements(elements) {
  const result = [];
  let listItems = [];

  function flushList() {
    if (listItems.length > 0) {
      result.push(<ul key={`ul-${result.length}`}>{listItems}</ul>);
      listItems = [];
    }
  }

  elements.forEach((el, i) => {
    if (el.type === 'table') {
      flushList();
      result.push(
        <div key={i} className="post-table-wrapper">
          <table className="post-table">
            <thead>
              <tr>
                {el.rows[0].map((cell, j) => (
                  <th key={j}>{renderInline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {el.rows.slice(1).map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, j) => (
                    <td key={j}>{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      return;
    }

    const line = el.text;
    if (line.startsWith('- ')) {
      listItems.push(<li key={i}>{renderInline(line.slice(2))}</li>);
    } else {
      flushList();
      if (line.startsWith('# ')) {
        result.push(<h1 key={i}>{line.slice(2)}</h1>);
      } else if (line.startsWith('## ')) {
        result.push(<h2 key={i}>{line.slice(3)}</h2>);
      } else if (line.startsWith('### ')) {
        result.push(<h3 key={i}>{line.slice(4)}</h3>);
      } else if (line.trim()) {
        result.push(<p key={i}>{renderInline(line)}</p>);
      }
    }
  });

  flushList();
  return result;
}

function BlogPostPage() {
  const { slug } = useParams();
  // Support old numeric IDs by mapping them
  const actualSlug = slug && !isNaN(slug) ? `static-${slug}` : slug;

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStatic, setIsStatic] = useState(false);

  useEffect(() => {
    async function loadPost() {
      setLoading(true);

      // 정적 글 확인
      if (actualSlug && STATIC_POSTS_DETAIL[actualSlug]) {
        setPost(STATIC_POSTS_DETAIL[actualSlug]);
        setIsStatic(true);
        setLoading(false);
        return;
      }

      // API에서 가져오기
      try {
        const result = await getBlogPostBySlug(actualSlug);
        if (result.success) {
          setPost(result.data);
          setIsStatic(false);
        } else {
          setPost(null);
        }
      } catch (error) {
        console.error('Failed to load post:', error);
        setPost(null);
      }
      setLoading(false);
    }

    loadPost();
  }, [actualSlug]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} - 로또운세 블로그`;
    }
  }, [post]);

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="post-loading">로딩 중...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="blog-post-page">
        <div className="post-not-found">
          <h1>게시글을 찾을 수 없습니다</h1>
          <Link to="/blog" className="back-link">블로그로 돌아가기</Link>
        </div>
      </div>
    );
  }

  const content = post.content || '';
  const elements = groupTableRows(content);

  const postTitle = post.title || '';
  const postExcerpt = post.excerpt || postTitle;
  const postUrl = `https://lottounse.kr/blog/${actualSlug}`;
  const publishDate = isStatic ? post.date : (post.published_at || '');
  const drawNo = post.draw_no || post.drawNo;

  // 회차별 SEO 키워드 (당첨번호 글인 경우)
  const seoTitle = drawNo
    ? `${drawNo}회 로또 당첨번호 결과 - 로또 ${drawNo}회 당첨금 안내`
    : `${postTitle} - 로또운세 블로그`;
  const seoDescription = drawNo
    ? `로또 ${drawNo}회 당첨번호, ${drawNo}회 로또 당첨금, 로또${drawNo}회 결과. ${postExcerpt}`
    : postExcerpt;
  const seoKeywords = drawNo
    ? `${drawNo}회 로또 당첨번호,로또 ${drawNo}회,로또${drawNo}당첨번호,로또${drawNo}회 결과,${drawNo}회 당첨금,로또 당첨번호 ${drawNo}회,lotto ${drawNo}`
    : '로또,운세,당첨번호,로또운세';

  // JSON-LD 구조화 데이터
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: postTitle,
    description: seoDescription,
    url: postUrl,
    datePublished: publishDate,
    keywords: seoKeywords,
    author: { '@type': 'Organization', name: '로또운세' },
    publisher: { '@type': 'Organization', name: '로또운세', url: 'https://lottounse.kr' }
  };

  return (
    <div className="blog-post-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />
        <meta property="og:title" content={postTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={postTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <link rel="canonical" href={postUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="post-header">
        <Link to="/blog" className="back-link">← 블로그로 돌아가기</Link>
        <div className="post-meta">
          <span className="post-category">{post.category}</span>
          <span className="post-date">
            {isStatic ? post.date : new Date(post.published_at).toLocaleDateString('ko-KR')}
          </span>
        </div>
        <h1 className="post-title">{post.title}</h1>
      </div>

      <AdBanner />

      <article className="post-content">
        {renderElements(elements)}
      </article>

      <AdBanner />

      <div className="post-footer">
        <Link to="/blog" className="back-btn">
          더 많은 글 보기
        </Link>
      </div>
    </div>
  );
}

export default BlogPostPage;
